import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import {
  ActionBlockModal,
  ActionRetryModal,
  CIDocForm,
  QuestionsChallengeForm,
  VerifyClaveUnicaLogin,
} from "./components";
import { Modal, SnackBar } from "@components";
import {
  FormBody,
  FormContentContainer,
  FormHeader,
  FullScreenLoader,
} from "@components";
import {
  QuestionsChallengeActionError,
  ValidationChallengePayload,
  ValidationChallengeResponse,
  GetQuestionsFormData,
  CIDocFormData,
} from "@interfaces";
import { apiGateway, useDoctorContext } from "@contexts";
import { GetQuestions } from "./components/GetQuestions";
import {
  ErrorType,
  onQuestionChallengeAxiosErrorInterpreter,
} from "./hooks/onQuestionChallengeError";
import {
  ENROLMENT_STEP,
  ENROLMENT_STEP_STATUS,
  useEnrolmentStatusContext,
} from "@contexts/EnrolmentStatusContext";
import { ClaveUnicaLogin } from "./components/ClaveUnicaLogin";
import { useLocation } from "react-router-dom";
import { AxiosError } from "axios";

export const ValidationChallengeScreen: React.FC = () => {
  const doctor = useDoctorContext();
  const location = useLocation();
  const { requestId } = location.state || {};
  const {
    currentStep,
    currentStepStatus,
    getEnrolmentStatus,
    setCurrentStep,
    setCurrentStepStatus,
  } = useEnrolmentStatusContext();

  const [showUserIsVerifiedSnackbar, setShowUserIsVerifiedSnackbar] =
    useState(false);

  const [showClaveUnicaErrorSnackbar, setShowClaveUnicaErrorSnackbar] =
    useState(false);

  const [questionsErrorNextStep, setQuestionsErrorNextStep] =
    useState<ENROLMENT_STEP | null>(null);
  const [questionsErrorNextStepStatus, setQuestionsErrorNextStepStatus] =
    useState<ENROLMENT_STEP_STATUS | null>(null);

  const [showValidateDoctorDataModal, setShowValidateDoctorDataModal] =
    useState(false);
  const [showValidateDoctorDataInputs, setShowValidateDoctorDataInputs] =
    useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockModalContent, setBlockModalContent] = useState<{
    title: string;
    description?: string | React.ReactNode;
  }>({ title: "" });
  const [showRetryModal, setShowRetryModal] = useState(false);
  const [retryModalContent, setRetryModalContent] = useState<{
    title: string;
    description?: string | React.ReactNode;
  }>({ title: "" });
  const [docNumErrorDescription, setDocNumErrorDescription] = useState<
    string | React.ReactNode | null
  >(null);

  const ciform = useForm<CIDocFormData>({
    defaultValues: {
      ciDocNum: "",
    },
  });

  const form = useForm<GetQuestionsFormData>({
    defaultValues: {
      name: doctor.name,
      firstSurname: doctor.lastName,
      secondSurname: "",
      birthdate: doctor.birthdate,
    },
  });

  const {
    mutate: createValidationChallenge,
    isPending: isCreatingValidationChallenge,
    data: questions,
  } = useMutation({
    mutationFn: async (
      validationChallengePayload: ValidationChallengePayload,
    ) => {
      const { questions } = await apiGateway<
        ValidationChallengeResponse,
        ErrorType
      >(
        {
          method: "post",
          url: "/enrolment/questions-challenge",
          data: validationChallengePayload,
        },
        onQuestionChallengeAxiosErrorInterpreter,
      );

      return questions;
    },
    onSuccess: async () => {
      setDocNumErrorDescription(null);
      await getEnrolmentStatus();
    },
    onError: async (error) => {
      if (error instanceof QuestionsChallengeActionError) {
        switch (error.action) {
          case "block":
            setBlockModalContent({
              title: error.name,
              description: error.description,
            });
            setShowBlockModal(true);
            if (error.nextStep && error.nextStepStatus) {
              setQuestionsErrorNextStep(error.nextStep);
              setQuestionsErrorNextStepStatus(error.nextStepStatus);
            }
            return;
          case "invalidInput":
            if (error.nextStep === "CI_DOC_NUM") {
              ciform.setError("ciDocNum", {
                type: "manual",
                message: error.name,
              });
              setDocNumErrorDescription(error.description ?? null);
            }
            if (error.nextStep === "QUESTIONS_CHALLENGE") {
              setRetryModalContent({
                title: error.name,
                description: error.description,
              });
              setShowRetryModal(true);
            }
            if (error.nextStep && error.nextStepStatus) {
              setCurrentStep(error.nextStep);
              setCurrentStepStatus(error.nextStepStatus);
            } else {
              await getEnrolmentStatus();
            }
            return;
          case "retry": {
            setRetryModalContent({
              title: error.name,
              description: error.description,
            });
            setShowRetryModal(true);
            if (error.nextStep && error.nextStepStatus) {
              setCurrentStep(error.nextStep);
              setCurrentStepStatus(error.nextStepStatus);
            } else {
              await getEnrolmentStatus();
            }
            return;
          }
          case "incompleteData":
            setShowValidateDoctorDataModal(true);
            setShowValidateDoctorDataInputs(true);
            if (error.nextStep && error.nextStepStatus) {
              setCurrentStep(error.nextStep);
              setCurrentStepStatus(error.nextStepStatus);
            } else {
              await getEnrolmentStatus();
            }
            return;
        }
      }
      const frontErrorMessage =
        error.message ?? "Ha ocurrido un error, por favor intenta nuevamente.";
      setDocNumErrorDescription(null);

      console.error(frontErrorMessage);
      setRetryModalContent({
        title: "Error al generar preguntas",
        description: frontErrorMessage,
      });
      setShowRetryModal(true);
      const errorData = (error as AxiosError<ErrorType>).response?.data;
      const nextStep = errorData?.nextStep;
      const nextStepStatus = errorData?.nextStepStatus;
      if (nextStep && nextStepStatus) {
        setCurrentStep(nextStep);
        setCurrentStepStatus(nextStepStatus);
      } else {
        await getEnrolmentStatus();
      }
    },
  });

  useEffect(() => {
    const getEnrolmentStatusCallback = async () => {
      if (!currentStep) {
        await getEnrolmentStatus();
      }
    };
    getEnrolmentStatusCallback();
  }, [currentStep, getEnrolmentStatus]);

  const hasRecoveredOrphanedChallenge = useRef(false);

  useEffect(() => {
    if (
      currentStep === ENROLMENT_STEP.QUESTIONS_CHALLENGE &&
      currentStepStatus === ENROLMENT_STEP_STATUS.STARTED &&
      !questions &&
      !isCreatingValidationChallenge &&
      !hasRecoveredOrphanedChallenge.current
    ) {
      hasRecoveredOrphanedChallenge.current = true;
      createValidationChallenge({});
    }
  }, [
    currentStep,
    currentStepStatus,
    questions,
    isCreatingValidationChallenge,
    createValidationChallenge,
  ]);

  if (
    currentStep === ENROLMENT_STEP.CLAVE_UNICA &&
    currentStepStatus === ENROLMENT_STEP_STATUS.NOT_STARTED
  )
    return (
      <>
        <ClaveUnicaLogin requestId={requestId} />
        <SnackBar
          snackBarState={{
            open: showClaveUnicaErrorSnackbar,
            type: "error",
          }}
          onClose={() => setShowClaveUnicaErrorSnackbar(false)}
          alerts={{
            error: "Error al validar identidad con clave única",
          }}
        />
      </>
    );
  if (
    currentStep === ENROLMENT_STEP.CLAVE_UNICA &&
    currentStepStatus === ENROLMENT_STEP_STATUS.STARTED
  )
    return (
      <>
        <VerifyClaveUnicaLogin
          setShowUserIsVerifiedSnackbar={setShowUserIsVerifiedSnackbar}
          setShowClaveUnicaErrorSnackbar={setShowClaveUnicaErrorSnackbar}
        />
      </>
    );
  if (currentStep === ENROLMENT_STEP.CI_DOC_NUM)
    return (
      <>
        <CIDocForm
          docNumErrorDescription={docNumErrorDescription}
          form={ciform}
        />
        <SnackBar
          snackBarState={{
            open: showUserIsVerifiedSnackbar,
            type: "success",
          }}
          onClose={() => setShowUserIsVerifiedSnackbar(false)}
          alerts={{
            success: "Identidad validada con clave única",
          }}
        />
      </>
    );
  if (
    currentStep === ENROLMENT_STEP.QUESTIONS_CHALLENGE &&
    currentStepStatus === ENROLMENT_STEP_STATUS.NOT_STARTED
  ) {
    return (
      <>
        <GetQuestions
          form={form}
          showValidateDoctorDataInputs={showValidateDoctorDataInputs}
          createValidationChallenge={createValidationChallenge}
          isCreatingValidationChallenge={isCreatingValidationChallenge}
        />
        <ActionBlockModal
          isOpen={showBlockModal}
          onClose={() => setShowBlockModal(false)}
          title={blockModalContent.title}
          description={blockModalContent.description}
          questionsErrorNextStep={questionsErrorNextStep}
          setQuestionsErrorNextStep={setQuestionsErrorNextStep}
          questionsErrorNextStepStatus={questionsErrorNextStepStatus}
          setQuestionsErrorNextStepStatus={setQuestionsErrorNextStepStatus}
        />
        <ActionRetryModal
          isOpen={showRetryModal}
          onClose={() => setShowRetryModal(false)}
          title={retryModalContent.title}
          description={retryModalContent.description}
          createValidationChallenge={createValidationChallenge}
        />
        <Modal
          title="Se requiere información adicional"
          content="Algunos datos del Registro Nacional de Prestadores Individuales de Salud no fueron encontrados. Por favor, completa los nuevos campos requeridos."
          isOpen={showValidateDoctorDataModal}
          onClose={() => setShowValidateDoctorDataModal(false)}
          buttonProps={{
            buttonText: "Aceptar",
            onClick: () => setShowValidateDoctorDataModal(false),
          }}
        />
        <SnackBar
          snackBarState={{
            open: showUserIsVerifiedSnackbar,
            type: "success",
          }}
          onClose={() => setShowUserIsVerifiedSnackbar(false)}
          alerts={{
            success: "Identidad validada con clave única",
          }}
        />
      </>
    );
  }
  if (
    currentStep === ENROLMENT_STEP.QUESTIONS_CHALLENGE &&
    currentStepStatus === ENROLMENT_STEP_STATUS.STARTED
  ) {
    if (questions) {
      return (
        <QuestionsChallengeForm
          questions={questions}
          setQuestionsErrorNextStep={setQuestionsErrorNextStep}
          setQuestionsErrorNextStepStatus={setQuestionsErrorNextStepStatus}
          setShowBlockModal={setShowBlockModal}
          setBlockModalContent={setBlockModalContent}
          setShowRetryModal={setShowRetryModal}
          setRetryModalContent={setRetryModalContent}
        />
      );
    }
    return (
      <>
        <FormHeader />
        <FormContentContainer flex={1} disableGutters>
          <FormBody justifyContent="center">
            <FullScreenLoader title="Cargando preguntas" />
          </FormBody>
        </FormContentContainer>
        <ActionBlockModal
          isOpen={showBlockModal}
          onClose={() => setShowBlockModal(false)}
          title={blockModalContent.title}
          description={blockModalContent.description}
          questionsErrorNextStep={questionsErrorNextStep}
          setQuestionsErrorNextStep={setQuestionsErrorNextStep}
          questionsErrorNextStepStatus={questionsErrorNextStepStatus}
          setQuestionsErrorNextStepStatus={setQuestionsErrorNextStepStatus}
        />
        <ActionRetryModal
          isOpen={showRetryModal}
          onClose={() => setShowRetryModal(false)}
          title={retryModalContent.title}
          description={retryModalContent.description}
          createValidationChallenge={createValidationChallenge}
        />
      </>
    );
  }
  return null;
};
